import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

type Indicator = { left: number; width: number };

/** Sliding accent underline for tab strips. Hover/focus preview the destination. */
export function useSlidingTabs(activeKey: string, ...deps: unknown[]) {
  const listRef = useRef<HTMLDivElement>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [indicator, setIndicator] = useState<Indicator>({ left: 0, width: 0 });
  const underlineKey = hoverKey ?? activeKey;

  useLayoutEffect(() => {
    function place() {
      const list = listRef.current;
      if (!list) return;
      const button =
        list.querySelector<HTMLElement>(`[data-tab="${CSS.escape(underlineKey)}"]`) ??
        list.querySelector<HTMLElement>(
          '[role="tab"][aria-selected="true"], button.is-active, button.active',
        );
      if (!button || !list.contains(button)) {
        setIndicator({ left: 0, width: 0 });
        return;
      }
      setIndicator({ left: button.offsetLeft, width: button.offsetWidth });
    }
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
    // Callers pass layout-affecting values (counts, labels) after activeKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underlineKey, ...deps]);

  return {
    listRef: listRef as RefObject<HTMLDivElement>,
    listProps: {
      onMouseLeave: () => setHoverKey(null),
    },
    tabProps: (key: string) => ({
      'data-tab': key,
      onMouseEnter: () => setHoverKey(key),
      onFocus: () => setHoverKey(key),
      onBlur: () => setHoverKey(null),
    }),
    indicatorStyle: {
      transform: `translateX(${indicator.left}px)`,
      width: indicator.width,
    } satisfies CSSProperties,
  };
}

export function TabUnderline({ style }: { style: CSSProperties }) {
  return <span className="tab-underline" aria-hidden="true" style={style} />;
}
